import React, { useMemo, useRef, useContext, useState } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import PieChart, {
    Series,
    Label,
    Connector,
    Legend,
    Tooltip,
    Export,
    Title,
    Font,
} from 'devextreme-react/pie-chart';
import Chart, { Label as ChartLabel, Legend as ChartLegend, Series as ChartSeries, Tooltip as ChartTooltip, ValueAxis } from 'devextreme-react/chart';
import { useGenderData } from '../contexts';
import { GenderFilterControls } from './GenderFilterControls';
import { DashboardCard, DashboardCardContext } from '../components/DashboardCard';
import { DashboardChartViewSelect } from '../components/DashboardChartViewSelect';
import { DASHBOARD_CHART_VIEW_CONFIG, DASHBOARD_DISTRIBUTION_COLORS, DashboardChartView, getDashboardDistributionColor } from '../types';

interface AggregatedGender {
    genderName: string;
    studentCount: number;
    percentage: number;
}

const GenderChartContent = ({ chartRef, cartesianChartRef, chartView, setChartView }: {
    chartRef: React.RefObject<PieChart>;
    cartesianChartRef: React.RefObject<Chart>;
    chartView: DashboardChartView;
    setChartView: (view: DashboardChartView) => void;
}) => {
    const { data, loading, error, grandTotal, filter } = useGenderData();
    const { isExpanded } = useContext(DashboardCardContext);

    const styleConfig = isExpanded ? {
        centerTitleSize: 42,
        centerSubSize: 18,
        innerRadius: 0.68,
        labelSize: 14
    } : {
        centerTitleSize: 32,
        centerSubSize: 14,
        innerRadius: 0.65,
        labelSize: 12
    };

    // Prepare metadata for export
    const exportMetadata = useMemo(() => {
        const yearsLabel = filter.schoolYears.length > 0
            ? filter.schoolYears.map(y => `${y}-${y + 1}`).join(', ')
            : 'All Years';
        
        return {
            title: 'Gender Distribution',
            subtitle: `Years: ${yearsLabel}`
        };
    }, [filter.schoolYears]);

    // Aggregate data across all selected years
    const aggregatedData = useMemo((): AggregatedGender[] => {
        if (!data || data.length === 0) return [];

        const genderMap = new Map<string, { count: number; displayOrder: number }>();

        data.forEach(item => {
            const existing = genderMap.get(item.genderName);
            if (existing) {
                existing.count += item.studentCount;
            } else {
                genderMap.set(item.genderName, {
                    count: item.studentCount,
                    displayOrder: item.genderDisplayOrder
                });
            }
        });

        const total = grandTotal?.totalStudents || 0;

        return Array.from(genderMap.entries())
            .map(([genderName, { count, displayOrder }]) => ({
                genderName,
                studentCount: count,
                percentage: total > 0 ? Math.round((count / total) * 100 * 10) / 10 : 0,
                displayOrder
            }))
            .sort((a, b) => a.displayOrder - b.displayOrder);
    }, [data, grandTotal]);

    const totalStudents = grandTotal?.totalStudents || 0;

    if (loading) {
        return <Skeleton variant="rounded" height={400} />;
    }

    if (error) {
        return (
            <Typography color="error" align="center">
                {error}
            </Typography>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Typography align="center" color="textSecondary">
                No gender data available for selected years
            </Typography>
        );
    }

    const formatPercentage = (percent: number): string => {
        const pct = percent * 100;
        if (pct > 0 && pct < 1) return '<1';
        return Math.round(pct).toString();
    };

    const centerTemplate = () => {
        return (
            <svg>
                <text 
                    textAnchor="middle" 
                    x="50%" 
                    y="45%" 
                    style={{ 
                        fontSize: `${styleConfig.centerTitleSize}px`, 
                        fontWeight: 'bold',
                        fill: '#000'
                    }}
                >
                    {totalStudents.toLocaleString()}
                </text>
                <text 
                    textAnchor="middle" 
                    x="50%" 
                    y="55%" 
                    style={{ 
                        fontSize: `${styleConfig.centerSubSize}px`,
                        fill: '#666'
                    }}
                >
                    Total Students
                </text>
            </svg>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <GenderFilterControls />
                <DashboardChartViewSelect dashboard="gender" value={chartView} onChange={setChartView} />
            </Box>
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                {(chartView === 'pie' || chartView === 'fullPie') ? <PieChart
                    ref={chartRef}
                    dataSource={aggregatedData}
                    type={chartView === 'fullPie' ? 'pie' : 'doughnut'}
                    innerRadius={styleConfig.innerRadius}
                    height="100%"
                    palette={DASHBOARD_DISTRIBUTION_COLORS}
                    centerRender={centerTemplate}
                >
                    <Title text={exportMetadata.title} subtitle={exportMetadata.subtitle}>
                        <Font size={18} weight={600} />
                    </Title>
                    <Series
                        argumentField="genderName"
                        valueField="studentCount"
                    >
                        <Label
                            visible={true}
                            position="outside"
                            customizeText={(pointInfo: { argumentText: string; valueText: string; percent: number }) => {
                                const displayPct = formatPercentage(pointInfo.percent);
                                return `${pointInfo.argumentText}\n${pointInfo.valueText} (${displayPct}%)`;
                            }}
                        >
                            <Font size={styleConfig.labelSize} />
                            <Connector visible={true} width={1} />
                        </Label>
                    </Series>
                    <Legend visible={false} />
                    <Tooltip
                        enabled={true}
                        customizeTooltip={(pointInfo: { argumentText: string; valueText: string; percent: number }) => {
                            const displayPct = formatPercentage(pointInfo.percent);
                            return {
                                text: `${pointInfo.argumentText}: ${pointInfo.valueText} (${displayPct}%)`
                            };
                        }}
                    />
                    <Export enabled={isExpanded} />
                </PieChart> : <Chart ref={cartesianChartRef} dataSource={aggregatedData} height="100%" rotated={chartView === 'horizontalBar'}
                    palette={DASHBOARD_DISTRIBUTION_COLORS} customizePoint={(args: any) => ({ color: getDashboardDistributionColor(args.index) })}>
                    <ChartSeries type="bar" argumentField="genderName" valueField="studentCount">
                        <ChartLabel visible customizeText={({ valueText }) => valueText} />
                    </ChartSeries>
                    <ValueAxis allowDecimals={false} />
                    <ChartLegend visible={false} />
                    <ChartTooltip enabled customizeTooltip={({ argumentText, valueText }) => ({ text: `${argumentText}: ${valueText}` })} />
                </Chart>}
            </Box>
        </Box>
    );
};

export const GenderChartCard = () => {
    const { filter } = useGenderData();
    const chartRef = useRef<PieChart>(null);
    const cartesianChartRef = useRef<Chart>(null);
    const [chartView, setChartView] = useState<DashboardChartView>(DASHBOARD_CHART_VIEW_CONFIG.gender.recommendedView);

    const handleExport = () => {
        const yearsLabel = filter.schoolYears.length > 0 ? filter.schoolYears.join('_') : 'AllYears';
        if (chartView === 'pie' || chartView === 'fullPie') chartRef.current?.instance.exportTo(`Gender_${yearsLabel}`, 'png');
        else cartesianChartRef.current?.instance.exportTo(`Gender_${yearsLabel}`, 'png');
    };

    return (
        <DashboardCard title="Gender" onExport={handleExport}>
            <GenderChartContent chartRef={chartRef} cartesianChartRef={cartesianChartRef}
                chartView={chartView} setChartView={setChartView} />
        </DashboardCard>
    );
};
